// =============================================================================
// ACT MATH TEST BANK INDEX
// Combines all test forms for Testing Mode
// =============================================================================

import { 
  SCALE_SCORE_TABLE as SCALE_SCORE_TABLE_2176CPRE, 
  questions_2176CPRE 
} from './testBank-2176CPRE.js';

import { 
  SCALE_SCORE_TABLE_25MC1, 
  questions_25MC1 
} from './testBank-25MC1.js';

import { 
  FORM_METADATA as META_25MC2, 
  questions_25MC2,
  SCALE_SCORE_TABLE_25MC2 
} from './testBank-25MC2.js';

import { 
  FORM_METADATA as META_25MC3, 
  questions_25MC3,
  SCALE_SCORE_TABLE_25MC3 
} from './testBank-25MC3.js';

import { 
  FORM_METADATA as META_25MC4, 
  questions_25MC4,
  SCALE_SCORE_TABLE_25MC4 
} from './testBank-25MC4.js';

import { 
  FORM_METADATA as META_J08, 
  questions_J08,
  SCALE_SCORE_TABLE_J08 
} from './testBank-J08.js';

import { 
  FORM_METADATA as META_25MC5, 
  questions_25MC5,
  SCALE_SCORE_TABLE_25MC5 
} from './testBank-25MC5.js';

// -----------------------------------------------------------------------------
// FORM METADATA (for forms that don't export it)
// -----------------------------------------------------------------------------
const META_2176CPRE = {
  formId: '2176CPRE',
  source: 'ACT Practice Test (Classic Format)',
  testType: 'Practice Test',
  format: 'Classic',
  totalQuestions: 60,
  scoredQuestions: 60,
  fieldTestPositions: [],
  timeMinutes: 60
};

const META_25MC1 = {
  formId: '25MC1',
  source: 'Preparing for the ACT 2025-2026',
  testType: 'Practice Test',
  format: 'Enhanced',
  totalQuestions: 45,
  scoredQuestions: 41,
  fieldTestPositions: [7, 16, 29, 40],
  timeMinutes: 50
};

// -----------------------------------------------------------------------------
// ALL TEST FORMS DATA
// -----------------------------------------------------------------------------
export const testFormsData = {
  '2176CPRE': {
    metadata: META_2176CPRE,
    questions: questions_2176CPRE,
    scaleTable: SCALE_SCORE_TABLE_2176CPRE,
    scaleTableFormat: 'minmax' // Uses {min, max, scale} format
  },
  '25MC1': {
    metadata: META_25MC1,
    questions: questions_25MC1,
    scaleTable: SCALE_SCORE_TABLE_25MC1
  },
  '25MC2': {
    metadata: META_25MC2,
    questions: questions_25MC2,
    scaleTable: SCALE_SCORE_TABLE_25MC2
  },
  '25MC3': {
    metadata: META_25MC3,
    questions: questions_25MC3,
    scaleTable: SCALE_SCORE_TABLE_25MC3
  },
  '25MC4': {
    metadata: META_25MC4,
    questions: questions_25MC4,
    scaleTable: SCALE_SCORE_TABLE_25MC4
  },
  'J08': {
    metadata: META_J08,
    questions: questions_J08,
    scaleTable: SCALE_SCORE_TABLE_J08
  },
  '25MC5': {
    metadata: META_25MC5,
    questions: questions_25MC5,
    scaleTable: SCALE_SCORE_TABLE_25MC5
  }
};

// -----------------------------------------------------------------------------
// TEST FORMS LIST (for dashboard display)
// -----------------------------------------------------------------------------
export const testFormsList = [
  {
    id: '2176CPRE',
    name: 'Form 2176CPRE',
    format: 'Classic',
    source: 'ACT Practice Test',
    questions: 60,
    scoredQuestions: 60,
    duration: 60,
    fieldTestPositions: [],
    available: true,
    hasRealQuestions: true
  },
  {
    id: '25MC1',
    name: 'Form 25MC1',
    format: 'Enhanced',
    source: 'Preparing for the ACT 2025-2026',
    questions: 45,
    scoredQuestions: 41,
    duration: 50,
    fieldTestPositions: [7, 16, 29, 40],
    available: true,
    hasRealQuestions: true
  },
  {
    id: '25MC2',
    name: 'Form 25MC2',
    format: 'Enhanced',
    source: 'Official ACT Prep Guide 2025-2026',
    questions: 45,
    scoredQuestions: 41,
    duration: 50,
    fieldTestPositions: [6, 18, 30, 38],
    available: true,
    hasRealQuestions: true
  },
  {
    id: '25MC3',
    name: 'Form 25MC3',
    format: 'Enhanced',
    source: 'Official ACT Prep Guide 2025-2026',
    questions: 45,
    scoredQuestions: 41,
    duration: 50,
    fieldTestPositions: [7, 17, 28, 39],
    available: true,
    hasRealQuestions: true
  },
  {
    id: '25MC4',
    name: 'Form 25MC4',
    format: 'Enhanced',
    source: 'Official ACT Prep Guide 2025-2026',
    questions: 45,
    scoredQuestions: 41,
    duration: 50,
    fieldTestPositions: [8, 16, 29, 40],
    available: true,
    hasRealQuestions: true
  },
  {
    id: 'J08',
    name: 'Form J08 (Oct 2025)',
    format: 'Released ACT',
    source: 'ACT October 2025 Release',
    questions: 41,
    scoredQuestions: 41,
    duration: 50,
    fieldTestPositions: [],
    available: true,
    hasRealQuestions: true
  },
  {
    id: '25MC5',
    name: 'Form 25MC5',
    format: 'Enhanced',
    source: 'ACT Practice Test 2 (2025-2026)',
    questions: 45,
    scoredQuestions: 41,
    duration: 50,
    fieldTestPositions: [8, 18, 28, 38],
    available: true,
    hasRealQuestions: true
  }
];

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Get questions for a specific test form
 * @param {string} formId - The form ID (e.g., '25MC3')
 * @returns {Array} Array of questions or empty array if form not found
 */
export function getTestQuestions(formId) {
  const form = testFormsData[formId];
  if (form) {
    return form.questions;
  }
  return [];
}

/**
 * Get only scored questions (excluding field test items)
 * @param {string} formId - The form ID
 * @returns {Array} Array of scored questions only
 */
export function getScoredQuestions(formId) {
  const questions = getTestQuestions(formId);
  return questions.filter(q => q.isScored !== false);
}

/**
 * Calculate scaled score for a test form
 * @param {string} formId - The form ID
 * @param {number} rawScore - Number of correct answers
 * @returns {number} Scaled score (1-36)
 */
export function calculateTestScore(formId, rawScore) {
  const form = testFormsData[formId];
  if (form && form.scaleTable) {
    // Handle minmax format (used by 2176CPRE)
    if (form.scaleTableFormat === 'minmax') {
      for (const row of form.scaleTable) {
        if (rawScore >= row.min && rawScore <= row.max) {
          return row.scale;
        }
      }
      return 1;
    }
    // Handle standard format (raw → scale)
    const entry = form.scaleTable.find(e => e.raw === rawScore);
    if (entry) return entry.scale;
    // Find closest if exact match not found
    const sorted = [...form.scaleTable].sort((a, b) => Math.abs(a.raw - rawScore) - Math.abs(b.raw - rawScore));
    return sorted[0]?.scale || 1;
  }
  // Default scoring table for forms without specific tables
  return Math.round(1 + (rawScore / 41) * 35);
}

/**
 * Check if a form has real questions loaded
 * @param {string} formId - The form ID
 * @returns {boolean}
 */
export function hasRealQuestions(formId) {
  return !!testFormsData[formId]?.questions?.length;
}

/**
 * Get form metadata
 * @param {string} formId - The form ID
 * @returns {Object|null}
 */
export function getFormMetadata(formId) {
  return testFormsData[formId]?.metadata || null;
}

/**
 * Get all questions from all forms
 * @returns {Array} Array of all questions
 */
export function getAllQuestions() {
  return Object.values(testFormsData).flatMap(form => form.questions || []);
}

/**
 * Get questions by category across all forms
 * @param {string} category - Category code (N, A, F, G, S, IES)
 * @returns {Array} Array of questions in that category
 */
export function getQuestionsByCategory(category) {
  return getAllQuestions().filter(q => q.category === category);
}

// -----------------------------------------------------------------------------
// STATS
// -----------------------------------------------------------------------------
export const testBankStats = {
  totalForms: testFormsList.length,
  formsWithQuestions: Object.keys(testFormsData).length,
  totalQuestions: Object.values(testFormsData).reduce((sum, form) => sum + (form.questions?.length || 0), 0),
  scoredQuestions: Object.values(testFormsData).reduce((sum, form) => {
    const questions = form.questions || [];
    return sum + questions.filter(q => q.isScored !== false).length;
  }, 0)
};

console.log(`[Math Test Bank] Loaded: ${testBankStats.formsWithQuestions} forms with ${testBankStats.totalQuestions} questions (${testBankStats.scoredQuestions} scored)`);
